import { createContext, useContext, useEffect, useMemo, useState } from 'react';

const UiContext = createContext(null);

const CLOSED = {
  searchOpen: false,
  calendarOpen: false,
  proOpen: false,
  accountOpen: false,
  helpOpen: false,
  detail: null,
  recurringOpen: false,
  recurCreate: null,
};

export function UiProvider({ children }) {
  const [ui, setUi] = useState({ ...CLOSED, query: '' });

  useEffect(() => {
    const openSearch = () => {
      setUi((current) => ({
        ...current,
        ...CLOSED,
        searchOpen: true,
      }));
    };
    document.addEventListener('deux:search', openSearch);
    return () => document.removeEventListener('deux:search', openSearch);
  }, []);

  const api = useMemo(
    () => ({
      ui,
      setQuery: (query) => setUi((current) => ({ ...current, query })),
      toggle: (key) =>
        setUi((current) => {
          const next = !current[key];
          return { ...current, ...CLOSED, [key]: next };
        }),
      close: (key) => setUi((current) => ({ ...current, [key]: false })),
      closeAll: () => setUi((current) => ({ ...current, ...CLOSED })),
      openDetail: (detail) =>
        setUi((current) => {
          const open = current.detail;
          const same =
            open &&
            open.id === detail.id &&
            open.zone === detail.zone &&
            open.date === detail.date &&
            open.pageId === detail.pageId &&
            open.listId === detail.listId;
          if (same) return { ...current, detail: null };
          return { ...current, ...CLOSED, detail };
        }),
      closeDetail: () => setUi((current) => (current.detail ? { ...current, detail: null } : current)),
      openRecurring: (draft) =>
        setUi((current) => ({
          ...current,
          ...CLOSED,
          recurringOpen: true,
          recurCreate: draft || null,
        })),
      openRecurCreate: (draft) =>
        setUi((current) => ({
          ...current,
          ...CLOSED,
          recurringOpen: current.recurringOpen,
          recurCreate: draft,
        })),
      closeRecurCreate: () => setUi((current) => ({ ...current, recurCreate: null })),
    }),
    [ui]
  );

  return <UiContext.Provider value={api}>{children}</UiContext.Provider>;
}

export function useUi() {
  const context = useContext(UiContext);
  if (!context) throw new Error('useUi must be used within UiProvider');
  return context;
}
